import React, { useState, useEffect } from 'react';
import Investment from './Investment';
import NewResearchItemModal from './NewResearchItemModal';
import { connect } from 'react-redux';
import { compose } from 'redux-starter-kit';
import api from 'api';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';
import { Button, BoxWrapper, Prompt } from 'components/Base';
import TextInput from 'components/TextInput/TextInput';
import Icons from 'components/Icons';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolderOutlined';
import { errHelper, Notify } from 'utils';
import { useDebouncedCallback } from 'use-debounce';
import Router from 'components/Router/Router';
import DownloadModal from './DownloadModal';
import Routes from 'consts/routes';
import Store from 'store/store';
import moment from 'moment';
import SearchGeneric from 'components/Fields/Search/SearchGeneric';
import { deleteLocalKeyStorageFuzzy, mapCipOptions } from './utils';
import UnsavedAnalysisModal from './UnsavedAnalysisModal';

const { useHistory } = Router;
const { selectors, actions } = Store;

const InvestmentAnalyser = ({
  updateCipOptions,
  updateResearchInvestments,
  investments,
  updateBenchmarks,
  updateBenchmarkConfig,
  getCipCategories,
  getMpsFirms,
  updatePerformancePeriod
}: any) => {
  const location = Router.useLocation() as any;
  const history = useHistory();
  const [backgroundChartsLoaded, setBackgroundChartsLoaded] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadingResearchOptions, setLoadingResearchOptions] = useState(false);
  const [loadingResearchItems, setLoadingResearchItems] = useState(false);
  const [showInvestment, setShowInvestment] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState<number | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [researchItems, setResearchItems] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [researchItemToDelete, setResearchItemToDelete] = useState('');
  const [newAnalysis, setNewAnalysis] = useState(false);
  const [unsavedAnalysisModalOpen, setUnsavedAnalysisModalOpen] =
    useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (location.state && 'selectedInvestment' in location.state) {
      setSelectedInvestment(location.state.selectedInvestment);
      if (location.state.selectedInvestment !== null) {
        setShowInvestment(true);
      }
    } else {
      setSelectedInvestment(null);
      setShowInvestment(false);
    }
  }, [location]);

  const loadCipOptions = () => {
    api.getCipRelations({ cip_thresholds: null }).then(({ data }) => {
      updateCipOptions(mapCipOptions(data));
    });
  };

  const loadResearchOptions = () => {
    if (!selectedInvestment) {
      updateResearchInvestments([]);
      return;
    }
    setLoadingResearchOptions(true);
    api.getBenchmarkConfig().then(response => {
      updateBenchmarkConfig(response);
      api.getBenchmarks().then(response => {
        updateBenchmarks(response);
        api
          .getResearchRelations({
            research_items: [Number(selectedInvestment)]
          })
          .then(response => {
            if (response.data[Number(selectedInvestment)]) {
              const investmentData = [
                ...response.data[Number(selectedInvestment)].funds,
                ...response.data[Number(selectedInvestment)].model_portfolios
              ];
              updateResearchInvestments(investmentData);
              setLoadingResearchOptions(false);
              setShowInvestment(true);
            } else {
              setLoadingResearchOptions(false);
              updateResearchInvestments([]);
              setShowInvestment(true);
              return;
            }
          });
      });
    });
  };

  const onResearchItemSave = (researchItem: any, index: number) => {
    const rItems = researchItems;
    rItems[index] = researchItem;
    setResearchItems([...rItems]);
  };

  const updateCurrentResearchItem = (researchItem: number | null) => {
    history.push(Routes.INVESTMENT_ANALYSER, {
      selectedInvestment: researchItem
    });
    setSelectedInvestment(researchItem);
  };

  const addResearchItem = (researchItem: any) => {
    updateCurrentResearchItem(researchItem.value);
    setShowInvestment(true);
    setResearchItems([...researchItems, researchItem]);
  };

  const saveNewResearchItem = (researchItem: any, index: number) => {
    setIsCreating(true);
    const id =
      Number(researchItem.value) === 0 ? null : Number(researchItem.value);
    api
      .createResearchItem({
        id,
        name: researchItem.name,
        quick_analysis: researchItem.quick_analysis,
        report_name: researchItem.report_name
      })
      .then(({ data }: any) => {
        id ? onResearchItemSave(data, index) : addResearchItem(data);
        if (!researchItem.quick_analysis) {
          Notify.success(
            `You have sucessfully ${id ? 'updated' : 'created'} ${
              researchItem.name
            }`
          );
        }
      })
      .catch(err => errHelper(err))
      .finally(() => {
        setIsCreating(false);
        setIsModalOpen(false);
      });
  };

  const deleteResearchItem = (researchItem: any) => {
    api
      .deleteResearchItem({ id: Number(researchItem.value) })
      .then(() => {
        const rItems = researchItems;
        rItems.splice(
          rItems.findIndex(item => item.value === researchItem.value),
          1
        );
        setResearchItems([...rItems]);
        deleteLocalKeyStorageFuzzy(`ia_case_${researchItem.value}`);
        Notify.success(`You have sucessfully deleted ${researchItem.name}`);
        setIsDeleteModalOpen(false);
      })
      .catch((err: any) => errHelper(err));
  };

  const saveCipThreshold = useDebouncedCallback((name, researchItem, index) => {
    saveNewResearchItem({ ...researchItem, name }, index);
  }, 1000);

  useEffect(() => {
    if (location.state && !!location.state.newAnalysis) {
      setNewAnalysis(true);
      saveNewResearchItem({ name: '', quick_analysis: true }, 0);
    }
    // eslint-disable-next-line
  }, [location.state]);

  useEffect(() => {
    updatePerformancePeriod();
    loadCipOptions();
    setLoadingResearchItems(true);
    api.getResearchItems().then((response: any) => {
      setResearchItems(response.data);
      setLoadingResearchItems(false);
    });
    getCipCategories();
    getMpsFirms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadResearchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInvestment]);

  const triggerDelete = (researchItem: any) => {
    setResearchItemToDelete(researchItem);
    setIsDeleteModalOpen(true);
  };

  return (
    <div style={{ marginTop: '2rem' }}>
      {loadingResearchOptions || loadingResearchItems ? (
        <LoaderSpinner isLoading={loadingResearchOptions} />
      ) : (
        <>
          {showInvestment ? (
            <>
              <Button
                type="submit"
                onClick={() => {
                  updateCurrentResearchItem(null);
                  setShowInvestment(false);
                }}
                style={{ marginBottom: '2rem' }}
              >
                Open Analysis
              </Button>
              <Button
                type="submit"
                onClick={() => setIsModalOpen(true)}
                style={{ marginBottom: '2rem', marginLeft: '1rem' }}
              >
                Save Analysis
              </Button>
              {investments.length > 0 && (
                <Button
                  type="submit"
                  disabled={!backgroundChartsLoaded}
                  isLoading={!backgroundChartsLoaded}
                  onClick={() => setIsDownloadModalOpen(true)}
                  style={{ marginBottom: '2rem', marginLeft: '1rem' }}
                >
                  Download
                </Button>
              )}
              <Investment
                selectedInvestment={selectedInvestment}
                researchItem={researchItems.find(
                  (item: any) => item.value === selectedInvestment
                )}
                backgroundChartsLoaded={setBackgroundChartsLoaded}
                newAnalysis={newAnalysis}
              />
            </>
          ) : (
            <div
              style={
                researchItems.length === 0
                  ? {
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column'
                    }
                  : {}
              }
            >
              {researchItems.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: '1rem',
                    marginTop: '1rem'
                  }}
                >
                  <CreateNewFolderIcon
                    htmlColor="#6b7280"
                    style={{ width: '36px', height: '36px' }}
                  />
                  <div style={{ fontWeight: 600 }}>No existing analysis</div>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '2rem'
                }}
              >
                {researchItems.length > 0 && (
                  <div style={{ width: '350px' }}>
                    <SearchGeneric
                      label="Search by name"
                      onSearch={setSearch}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', gap: '1rem' }}>
                  {researchItems.filter(
                    (item: any) => item.quick_analysis === true
                  ).length > 0 && (
                    <Button
                      type="submit"
                      onClick={() => setUnsavedAnalysisModalOpen(true)}
                    >
                      Unsaved Analysis
                    </Button>
                  )}
                  <Button
                    disabled={isCreating}
                    type="submit"
                    onClick={() =>
                      saveNewResearchItem({ name: '', quick_analysis: true }, 0)
                    }
                  >
                    New Analysis
                  </Button>
                </div>
              </div>
              {researchItems
                .filter(
                  (item: any) =>
                    item.quick_analysis === false &&
                    item.name.toLowerCase().includes(search.toLowerCase())
                )
                .sort((a, b) => (a.created_at > b.created_at ? -1 : 1))
                .map((researchItem, i) => (
                  <BoxWrapper key={i} style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex' }}>
                      <div
                        className="cipThresholdBar"
                        style={{ width: '300px' }}
                      >
                        <TextInput
                          label={`Analysis Name`}
                          onChange={(e: string) =>
                            saveCipThreshold(e, researchItem, i)
                          }
                          value={researchItem.name}
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <TextInput
                          label={`Created on`}
                          // eslint-disable-next-line @typescript-eslint/no-empty-function
                          onChange={() => {}}
                          value={moment(researchItem.created_at).format(
                            'DD/MM/YYYY'
                          )}
                          editable={false}
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          justifyContent: 'flex-end',
                          padding: '2rem',
                          gap: '10px'
                        }}
                      >
                        <div
                          className="cipThresholdDelete"
                          onClick={() => triggerDelete(researchItem)}
                        >
                          <Icons.DeleteIcon />
                        </div>
                        <Button
                          type="submit"
                          onClick={() =>
                            updateCurrentResearchItem(researchItem.value)
                          }
                          style={{ marginLeft: '1rem' }}
                        >
                          View Analysis
                        </Button>
                      </div>
                    </div>
                  </BoxWrapper>
                ))}
              <Prompt
                isOpen={isDeleteModalOpen}
                title={`Are you sure you want to delete this Investment Analysis?`}
                isProcessing={false}
                confirmAction={() => deleteResearchItem(researchItemToDelete)}
                cancelAction={() => setIsDeleteModalOpen(false)}
              />
            </div>
          )}
          <NewResearchItemModal
            initialValues={{
              name: researchItems
                ? (
                    researchItems.find(
                      (item: any) => item.value === selectedInvestment
                    ) || {}
                  ).name || ''
                : ''
            }}
            isModalOpen={isModalOpen}
            researchItem={researchItems.find(
              (item: any) => item.value === selectedInvestment
            )}
            onSubmit={(values: any) =>
              saveNewResearchItem(
                {
                  ...researchItems.find(
                    (item: any) => item.value === selectedInvestment
                  ),
                  name: values.name,
                  quick_analysis: false
                },
                researchItems.findIndex(
                  (item: any) => item.value === selectedInvestment
                )
              )
            }
            onClose={() => setIsModalOpen(false)}
          />
          <DownloadModal
            initialValues={{
              name: researchItems
                ? (
                    researchItems.find(
                      (item: any) => item.value === selectedInvestment
                    ) || {}
                  ).report_name || ''
                : ''
            }}
            isModalOpen={isDownloadModalOpen}
            selectedInvestment={selectedInvestment}
            researchItem={researchItems.find(
              (item: any) => item.value === selectedInvestment
            )}
            onClose={() => setIsDownloadModalOpen(false)}
            saveNewResearchItem={(name: any) =>
              saveNewResearchItem(
                {
                  ...researchItems.find(
                    (item: any) => item.value === selectedInvestment
                  ),
                  report_name: name,
                  quick_analysis: researchItems.find(
                    (item: any) => item.value === selectedInvestment
                  ).quick_analysis
                },
                researchItems.findIndex(
                  (item: any) => item.value === selectedInvestment
                )
              )
            }
          />
        </>
      )}
      <UnsavedAnalysisModal
        isModalOpen={unsavedAnalysisModalOpen}
        onClose={() => setUnsavedAnalysisModalOpen(false)}
        researchItems={researchItems.filter(
          (item: any) => item.quick_analysis === true
        )}
        updateCurrentResearchItem={updateCurrentResearchItem}
        triggerDelete={triggerDelete}
      />
    </div>
  );
};

export default compose(
  connect(
    (state: any) => ({
      investments:
        selectors.investmentAnalyserSelectors.checkedResearchInvestments(state)
    }),
    {
      updateCipOptions: actions.investmentAnalyserActions.updateCipOptions,
      updateResearchInvestments:
        actions.investmentAnalyserActions.updateResearchInvestments,
      updateBenchmarks: actions.metaActions.updateBenchmarks,
      updateBenchmarkConfig: actions.metaActions.updateBenchmarkConfig,
      getCipCategories: actions.cipActions.getCipCategories,
      getMpsFirms: actions.cipActions.getMpsFirms,
      updatePerformancePeriod:
        actions.investmentAnalyserActions.updatePerformancePeriod
    }
  )
)(InvestmentAnalyser);
